import React, { useCallback, useRef, useState } from 'react'
import styles from './Engine.module.css'
import { Form } from 'react-final-form'
import { EngineForm } from './Engine'
import { InputFormField } from '../../common/components/Form/InputFormField/InputFormField'
import { SelectFormField } from '../../common/components/Form/SelectFormField/SelectFormField'
import { getLanguageOptions } from './EngineHelper'
import { Button } from '../../common/components/button/Button'
import { t } from 'i18next'
import { Instructions } from './Instructions'
import classNames from 'classnames'
import { ThemeSelector } from './ThemeSelector'
import { FileField } from './FileField'
import { TextAreaFormField } from '../../common/components/Form/TextAreaFormField/TextAreaFormField'
import { useNotification } from '../../contexts/NotificationContext'
import { ToggleFormField } from '../../common/components/Form/ToggleFormField/ToggleFormField'

interface Props {
  initialValues: Partial<EngineForm>
  validate: (values: EngineForm) => Partial<EngineForm>
  onSubmit: (values: EngineForm) => void
  has3D: boolean
  currentLanguage: string
}

export const InstructionsAndForm: React.FC<Props> = ({ initialValues, validate, onSubmit, has3D, currentLanguage }) => {
  const [fileError, setFileError] = useState<string | undefined>()
  const [readyTasks, setReadyTasks] = useState(false)
  const formModifier = useRef<Function>()
  const { notifyInfo } = useNotification()

  const internalOnSubmit = useCallback(
    (values: EngineForm) => {
      console.log('subsub')
      console.log(values)
      if (
        (values.inputFiles === undefined || values.inputFiles.length < 1) &&
        (values.webSources ?? '').indexOf('https://') < 0 &&
        (values.freeText ?? '').length < 10
      ) {
        notifyInfo({
          //customDismissLabel: t('engine.error.missing_source', ''),
          title: t('engine.error.missing_source.title', 'Missing source(s) for game generation'),
          content: (
            <div className={styles.notificationContent}>
              {t(
                'engine.error.missing_source.content',
                'Please provide content to at least one of the possible sources. A document, a valid link or valid content to the free text field.',
              )}
            </div>
          ),
        })
        return
      }
      setFileError(undefined)
      onSubmit(values)
    },
    [onSubmit],
  )

  const handleThemeSelect = useCallback((theme: string) => {
    if (formModifier.current) {
      formModifier.current('template', theme)
    }
  }, [])

  return (
    <div className={styles.stepWrapper}>
      <div className={styles.stepTitle}>{t('engine.login_step.lets_create', "Let's create your game")}</div>
      <Instructions initialOpen={false} />
      <Form<EngineForm>
        onSubmit={internalOnSubmit}
        initialValues={initialValues}
        validate={validate}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value)
          },
        }}
      >
        {({
          handleSubmit,
          submitting,
          values,
          errors,
          form: {
            mutators: { setValue },
          },
        }) => {
          formModifier.current = setValue
          return (
            <form id='ENGINE_FORM' onSubmit={handleSubmit} className={styles.formContainer}>
              <div style={{ display: 'none' }}>
                <InputFormField
                  hidden
                  srOnlyLabel
                  name='instructorToAdd'
                  label={t('engine.user_email', 'Your email')}
                />
                <InputFormField hidden srOnlyLabel name='generatorPin' label={t('engine.pin_code', 'Pin code')} />
              </div>
              <div style={{ display: 'none' }}>
                <InputFormField
                  className={styles.largerInput}
                  labelClassName={styles.boldLabel}
                  name='gameName'
                  label={t('engine.game_name', 'Game name')}
                />
                <ThemeSelector has3D={has3D} onSelectTheme={handleThemeSelect} currentLanguage={currentLanguage} />
                <InputFormField
                  className={styles.hidden}
                  srOnlyLabel
                  name='template'
                  label={t('engine.theme_label', 'Theme')}
                  placeholder={t('engine.theme_placeholder', 'Select theme')}
                />
                <InputFormField
                  className={styles.largerInput}
                  labelClassName={styles.boldLabel}
                  name='targetGroup'
                  label={t('engine.target_group', 'Target group')}
                />
              </div>
              <SelectFormField
                labelClassName={styles.boldLabel}
                name='language'
                label={t('engine.language_label', 'Language')}
                placeholder={t('engine.language_placeholder', 'Select language')}
                options={getLanguageOptions()}
              />
              <ToggleFormField
                name={'readyTasks'}
                label={t('engine.doc_contains_ready_tasks.title', 'Does your source document contain ready tasks?')}
                description={t(
                  'engine.doc_contains_ready_tasks.disabled',
                  'No, I have content source(s) from which Saige should create tasks',
                )}
                descriptionChecked={t(
                  'engine.doc_contains_ready_tasks.enabled',
                  'Yes, I have a document with ready tasks that I want to convert into a Seppo game.',
                )}
                onChange={(checked) => {
                  setReadyTasks(!!checked)
                }}
              />
              <div className={styles.sourcesArea}>
                <div>
                  <div
                    style={{
                      color: 'var(--grey-700)',
                      display: 'inline-block',
                      marginBottom: '10px',
                      fontWeight: 'bold',
                    }}
                  >
                    {t('engine.material_files_placeholder', 'Material file(s)')}
                  </div>
                  <div className={classNames(styles.fileWrapper, fileError && styles.fileWrapperWithError)}>
                    <FileField name='inputFiles' />
                    {fileError && <div>{fileError}</div>}
                  </div>
                </div>
                {!readyTasks && (
                  <div className={styles.sourcesBottom}>
                    <TextAreaFormField
                      label={t('engine.web_links', 'Web link(s)')}
                      labelClassName={styles.boldLabel}
                      name='webSources'
                      className={styles.textAreaField}
                      placeholder={t('engine.web_links_placeholder', 'Enter list of web links')}
                      maxLength={1024}
                    />

                    <TextAreaFormField
                      name='freeText'
                      label={t('engine.free_text_input_label', 'Plain text / Free prompt')}
                      placeholder={t(
                        'engine.free_text_input_placeholder',
                        'Paste textual content here or describe in your own words what kind of game you want to start creating.',
                      )}
                      fieldContainerClassName={styles.descriptionFieldContainer}
                      labelClassName={styles.boldLabel}
                      className={styles.textAreaField}
                      maxLength={20000}
                    />
                  </div>
                )}
              </div>
              <Button className={styles.continueButton} variant={'big'} disabled={submitting} type='submit'>
                {t('engine.generate_button', 'Continue')}
              </Button>
            </form>
          )
        }}
      </Form>
    </div>
  )
}
